// 메쉬·지오메트리 도우미: 그림자 켠 메쉬, 메쉬 합치기, 구 캐시, 회전체.
import * as THREE from 'three';

/** 그림자를 드리우고 받는 메쉬. */
export function mk(geo: THREE.BufferGeometry, m: THREE.Material): THREE.Mesh {
  const o = new THREE.Mesh(geo, m);
  o.castShadow = true;
  o.receiveShadow = true;
  return o;
}

/** 그림자 드리우기만 선택하는 메쉬 (프로토타입의 part). */
export function part(geo: THREE.BufferGeometry, mat: THREE.Material, cast = false): THREE.Mesh {
  const o = new THREE.Mesh(geo, mat);
  o.castShadow = cast;
  return o;
}

/** 지오메트리 하나에 변형을 적용해 둔 임시 메쉬 (합치기 재료). */
export const T = (geo: THREE.BufferGeometry, f?: (m: THREE.Mesh) => void): THREE.Mesh => {
  const m = new THREE.Mesh(geo);
  if (f) f(m);
  return m;
};

/** 여러 메쉬를 월드 변형까지 적용해 지오메트리 하나로 합친다 (드로우 콜 줄이기). position·normal만 남긴다. */
export function mergeGeo(list: THREE.Mesh[]): THREE.BufferGeometry {
  let vc = 0, ic = 0;
  const parts: THREE.BufferGeometry[] = [];
  list.forEach((m) => {
    m.updateWorldMatrix(true, false);
    const g = m.geometry.clone();
    g.applyMatrix4(m.matrixWorld);
    parts.push(g);
    vc += g.attributes.position!.count;
    ic += g.index ? g.index.count : g.attributes.position!.count;
  });
  const pos = new Float32Array(vc * 3), nor = new Float32Array(vc * 3), idx = new Uint32Array(ic);
  let vo = 0, io = 0;
  parts.forEach((g) => {
    pos.set(g.attributes.position!.array as Float32Array, vo * 3);
    nor.set(g.attributes.normal!.array as Float32Array, vo * 3);
    const n = g.attributes.position!.count;
    if (g.index) {
      const ia = g.index.array;
      for (let i = 0; i < ia.length; i++) idx[io + i] = ia[i]! + vo;
      io += ia.length;
    } else {
      for (let i = 0; i < n; i++) idx[io + i] = vo + i;
      io += n;
    }
    vo += n;
    g.dispose();
  });
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  geo.setIndex(new THREE.BufferAttribute(idx, 1));
  return geo;
}

const geoCache: Record<string, THREE.BufferGeometry> = {};

/** 공유 구 지오메트리 (같은 크기·분할이면 하나를 재사용). */
export const S = (r: number, w?: number, h?: number): THREE.SphereGeometry => {
  const k = 's' + r + '_' + (w || 24) + '_' + (h || 16);
  return (geoCache[k] ||= new THREE.SphereGeometry(r, w || 24, h || 16)) as THREE.SphereGeometry;
};

/** 공유 가는 원기둥 (얼굴 선 등). */
export const lineGeo = (l: number): THREE.CylinderGeometry =>
  (geoCache['l' + l] ||= new THREE.CylinderGeometry(0.009, 0.009, l, 6)) as THREE.CylinderGeometry;

/**
 * three r128과 같은 삼각형 구성의 원뿔. 0.169의 ConeGeometry는 꼭짓점의 면적 0 삼각형을 빼는데,
 * 프로토타입의 EdgesGeometry 모서리 선은 그 삼각형이 있을 때 기준이라 선 개수가 달라진다 (빙하 소품).
 * 또 0.169는 높이 칸이 2 이상인 원뿔에서 모든 칸의 삼각형을 절반씩 빼서 줄무늬로 구멍이 난다 (라군 화산).
 * 높이 칸이 1인 원뿔만 나중에 ConeGeometry로 바꿔도 된다.
 */
export function legacyCone(radius: number, height: number, radialSegments: number, heightSegments: number, openEnded = false): THREE.ConeGeometry {
  const g = new THREE.ConeGeometry(radius, height, radialSegments, heightSegments, openEnded);
  const torso = g.groups[0]!, rest = Array.from(g.index!.array).slice(torso.count), full: number[] = [];
  const at = (y: number, x: number) => y * (radialSegments + 1) + x;
  for (let x = 0; x < radialSegments; x++) for (let y = 0; y < heightSegments; y++) {
    const a = at(y, x), b = at(y + 1, x), c = at(y + 1, x + 1), d = at(y, x + 1);
    full.push(a, b, d, b, c, d);
  }
  g.setIndex([...full, ...rest]);
  g.clearGroups();
  g.addGroup(0, full.length, 0);
  if (rest.length) g.addGroup(full.length, rest.length, 2);
  return g;
}

/** [x, y] 점 목록으로 만드는 회전체. */
export const lathe = (pts: [number, number][], seg?: number): THREE.LatheGeometry =>
  new THREE.LatheGeometry(pts.map(([x, y]) => new THREE.Vector2(x, y)), seg || 24);

/**
 * 움직이지 않는 장식 그룹을 재질(그리고 그림자 여부)별로 한 메쉬씩 합쳐 into에 넣고, 원래 그룹은 떼어 낸다 (드로우 콜 줄이기).
 * 프로토타입 842–848 (세 방 공용).
 */
export function bakeByMaterial(group: THREE.Object3D, into: THREE.Object3D): THREE.Mesh[] {
  const buckets = new Map<string, { mat: THREE.Material; cast: boolean; list: THREE.Mesh[] }>();
  group.traverse((o) => {
    if (!(o as THREE.Mesh).isMesh) return;
    const m = o as THREE.Mesh, mat = m.material as THREE.Material, k = mat.uuid + (m.castShadow ? 'c' : '');
    if (!buckets.has(k)) buckets.set(k, { mat, cast: m.castShadow, list: [] });
    buckets.get(k)!.list.push(m);
  });
  const out: THREE.Mesh[] = [];
  buckets.forEach((b) => { const m = new THREE.Mesh(mergeGeo(b.list), b.mat); m.castShadow = b.cast; m.receiveShadow = true; into.add(m); out.push(m); });
  group.parent?.remove(group);
  return out;
}
